import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type { FeedPage } from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { webApi } from "@web-shared/lib/web-api";
import { useIsNarrow } from "@web-shared/hooks/useIsNarrow";
import { FeedCardView, feedTrack } from "./FeedCardView";
import { FeedRow } from "./FeedRow";

/** Pages of cards carry presigned URLs that live at least an hour. */
export const CARDS_STALE_MS = 30 * 60_000;

/**
 * An endless list of feed cards from any core API path that answers a
 * FeedPage (the feed, a profile tab). Everything loaded is the player's queue.
 */
export function CardList({
  queryKey,
  path,
  empty,
  ranked = false,
  hide,
}: {
  /** A card shown elsewhere on the page (the featured hero) — left out here. */
  hide?: string | null;
  /** Mark the first ten as trending (Discover's own order). */
  ranked?: boolean;
  queryKey: readonly unknown[];
  /** Without the cursor; `?` or `&` is added. */
  path: string;
  empty: ReactNode;
}) {
  const list = useInfiniteQuery({
    queryKey: ["web", ...queryKey],
    staleTime: CARDS_STALE_MS,
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      webApi<FeedPage>(
        pageParam ? `${path}${path.includes("?") ? "&" : "?"}cursor=${encodeURIComponent(pageParam)}` : path,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const cards = useMemo(
    () => (list.data?.pages.flatMap((p) => p.items) ?? []).filter((c) => c.id !== hide),
    [list.data, hide],
  );
  const queue = useMemo(() => cards.filter((c) => c.audioUrl !== "").map((c) => feedTrack(c)), [cards]);
  // One layout in the DOM, not both behind `sm:hidden` / `hidden sm:block`:
  // the hidden one still built every card, twice the page for a phone to hold.
  const narrow = useIsNarrow();

  if (list.isPending) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-44 w-full rounded-2xl" />
        ))}
      </div>
    );
  }
  if (list.isError) {
    return (
      <EmptyState>
        <p className="text-removed">Could not load this.</p>
        <Button size="sm" variant="outline" onClick={() => void list.refetch()}>
          Try again
        </Button>
      </EmptyState>
    );
  }
  if (cards.length === 0) return <EmptyState>{empty}</EmptyState>;
  return (
    <>
      {narrow ? (
        /* Mobile: compact rows */
        <div>
          {cards.map((card) => (
            <FeedRow
              key={card.id}
              card={card}
              queue={queue}
            />
          ))}
        </div>
      ) : (
        /* Desktop: full cards */
        <div className="space-y-3">
          {cards.map((card, i) => (
            <FeedCardView
              key={card.id}
              card={card}
              queue={queue}
              trending={ranked ? i + 1 : undefined}
            />
          ))}
        </div>
      )}
      <LoadMore
        hasMore={list.hasNextPage}
        loading={list.isFetchingNextPage}
        onMore={() => void list.fetchNextPage()}
      />
    </>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-strong p-8 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

/** Fetches the next page when it scrolls into view; a button for the rest. */
export function LoadMore({
  hasMore,
  loading,
  onMore,
}: {
  hasMore: boolean;
  loading: boolean;
  onMore: () => void;
}) {
  const sentinel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !loading) onMore();
      },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, onMore]);
  if (!hasMore) return null;
  return (
    <div ref={sentinel} className="flex justify-center py-4">
      {loading ? (
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      ) : (
        <Button size="sm" variant="ghost" onClick={onMore}>
          Load more
        </Button>
      )}
    </div>
  );
}
